import type { ReactNode } from 'react'
import { useId } from 'react'
import { Clock, Flag } from 'lucide-react'
import { Avatar } from '@/components/play'
import type { RaceGender } from '@/lib/watch/graphics'
import type { Race } from '@/lib/watch/races'
import { cn } from '@/lib/utils'
import { PreviewLabel } from '../HostDock'
import { GENDER_GLYPH } from './glyphs'
import './broadcast.css'

/**
 * Broadcast elements' parts (docs/features/badger-cohost.md, Primitives), on Play tokens: an athlete's photo or
 * initials as the map's card draws them (AthleteCard: Play Avatar, `auto` tone keyed by the athlete), their race in its
 * course colour, a bib, a place, progress, a stat, a row of a board and the panel around a board. Elements compose them.
 * Icons over words (design/DESIGN.md): each part shows numbers and glyphs ("2 ♀", "#434", a bar, "4m") and keeps the
 * words for assistive technology ("2nd woman", "Bib 434", "6.2 of 10.4 km", "Seen 4 min ago").
 */

const kilometres = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

/** A distance on a board or a card, to a tenth of a kilometre ("14.2"). */
export function formatKm(km: number): string {
  return kilometres.format(Math.max(0, km))
}

const ordinals = new Intl.PluralRules('en-GB', { type: 'ordinal' })
const SUFFIX: Partial<Record<Intl.LDMLPluralRule, string>> = { one: 'st', two: 'nd', few: 'rd' }

/** 1st, 2nd, 3rd, 4th, 11th, 21st. */
export function ordinal(place: number): string {
  return `${place}${SUFFIX[ordinals.select(place)] ?? 'th'}`
}

const GENDER_NOUN: Record<RaceGender, string> = { female: 'woman', male: 'man' }

/** A place in words, for assistive technology: "3rd woman" within their gender, else "3rd overall". */
export function placeWords(place: number, gender: RaceGender | null): string {
  return `${ordinal(place)} ${gender ? GENDER_NOUN[gender] : 'overall'}`
}

/** An age as a glyph's caption reads it: "now", "12s", "4m", "2h" (`formatAge` says it in words). */
export function shortAge(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000))
  if (seconds < 5) return 'now'
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  return `${Math.floor(minutes / 60)}h`
}

export type PortraitSize = 'card' | 'row' | 'line'

/**
 * An athlete's photo, or their initials on their own tone while it loads or without one (as the map's card): `card`
 * leads a lower third, `row` a board's row, `line` a one-line lower third. Decorative: their name is beside it.
 */
export function Portrait({ athlete, size = 'card' }: { athlete: { id: string; name: string; photoUrl: string | null }; size?: PortraitSize }) {
  return (
    <Avatar
      name={athlete.name}
      src={athlete.photoUrl}
      size={size === 'line' ? 'xs' : size === 'row' ? 'sm' : 'default'}
      tone="auto"
      toneKey={athlete.id}
      className={cn('bc-portrait', `bc-portrait--${size}`)}
    />
  )
}

/** Their race: a swatch in its course colour and its short label ("21 km"). */
export function RaceTag({ race, className }: { race: Pick<Race, 'label' | 'color'>; className?: string }) {
  return (
    <span className={cn('bc-race', className)}>
      <span className="bc-race__swatch" style={{ background: race.color }} aria-hidden="true" />
      {race.label}
    </span>
  )
}

/** A bib as "#434", said "Bib 434". */
export function Bib({ bib, className }: { bib: string; className?: string }) {
  return (
    <span className={cn('bc-bib', className)}>
      <span aria-hidden="true">#{bib}</span>
      <span className="sr-only">Bib {bib}</span>
    </span>
  )
}

/** A place on an ink tag: the number, heavy, with Venus or Mars when it is within their gender ("2 ♀"), said "2nd woman". */
export function Place({ place, gender, className }: { place: number; gender: RaceGender | null; className?: string }) {
  const Glyph = gender ? GENDER_GLYPH[gender] : null
  return (
    <span className={cn('bc-place', className)}>
      <strong className="bc-place__rank" aria-hidden="true">
        {place}
      </strong>
      {Glyph && <Glyph aria-hidden="true" className="bc-place__glyph" />}
      <span className="sr-only">{placeWords(place, gender)}</span>
    </span>
  )
}

/**
 * How far along their race: a bar in the race's colour beside the kilometres ("6.2 km"), said "6.2 of 10.4 km". Without
 * the race's length, the kilometres alone.
 */
export function Progress({ km, lengthKm, color, className }: { km: number; lengthKm: number | null; color?: string | null; className?: string }) {
  const share = lengthKm ? Math.min(1, Math.max(0, km / lengthKm)) : null
  return (
    <span className={cn('bc-progress', className)}>
      {share !== null && (
        <span className="bc-progress__track" aria-hidden="true">
          <span className="bc-progress__fill" style={{ width: `${share * 100}%`, ...(color ? { background: color } : {}) }} />
        </span>
      )}
      <span className="bc-stat" aria-hidden="true">
        <strong className="bc-stat__value">{formatKm(km)}</strong> <span className="bc-stat__rest">km</span>
      </span>
      <span className="sr-only">{lengthKm ? `${formatKm(km)} of ${formatKm(lengthKm)} km` : `${formatKm(km)} km`}</span>
    </span>
  )
}

/** A number that leads (`value`, heavy) with what follows it ("of 21.1 km"). */
export function BroadcastStat({ value, rest, className }: { value: ReactNode; rest?: ReactNode; className?: string }) {
  return (
    <span className={cn('bc-stat', className)}>
      <strong className="bc-stat__value">{value}</strong>
      {rest && (
        <>
          {' '}
          <span className="bc-stat__rest">{rest}</span>
        </>
      )}
    </span>
  )
}

/** Over the host's own canvas only: the element isn't on air (the console's hover), as a camera preview says. */
export function PreviewMark() {
  return <PreviewLabel className="bc-preview" />
}

export interface BroadcastPanelProps {
  title: ReactNode
  /** Beside the title, such as the board's race. */
  aside?: ReactNode
  /** Under the rows: where the numbers come from and how fresh they are. */
  footnote?: ReactNode
  preview?: boolean
  compact?: boolean
  children: ReactNode
  className?: string
}

/** The panel's surface: a title, its content and a footnote, white on the overlay shadow, from the canvas's right edge. */
export function BroadcastPanel({ title, aside, footnote, preview = false, compact = false, children, className }: BroadcastPanelProps) {
  const titleId = useId()
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className={cn('bc-panel', className)}
      data-preview={preview || undefined}
      data-compact={compact || undefined}
    >
      {preview && <PreviewMark />}
      <div className="bc-panel__head">
        <p id={titleId} className="bc-panel__title">
          {title}
        </p>
        {aside}
      </div>
      {children}
      {footnote && <p className="bc-panel__foot">{footnote}</p>}
    </div>
  )
}

export interface BroadcastRowProps {
  rank: number
  athlete: { id: string; name: string; photoUrl: string | null }
  /** Kilometres covered. */
  km: number
  /** Behind the first, in kilometres; null or 0 shows no gap (the first, a later finisher). */
  behindKm: number | null
  finished: boolean
  stale: boolean
  /** How old their newest fix is, for a stale row's words ("6 min ago"). */
  age?: string | null
}

/**
 * One row of a board: rank, photo, name, kilometres and the gap to the first. A finisher's row carries the flag, a
 * stale one the clock and dims, each said in words for assistive technology too.
 */
export function BroadcastRow({ rank, athlete, km, behindKm, finished, stale, age }: BroadcastRowProps) {
  const gap = !finished && behindKm !== null && behindKm > 0 ? `+${formatKm(behindKm)}` : null
  return (
    <li className="bc-row" data-stale={(stale && !finished) || undefined} data-finished={finished || undefined}>
      <span className="bc-row__rank">{rank}</span>{' '}
      <Portrait athlete={athlete} size="row" />
      <span className="bc-row__name">{athlete.name}</span>{' '}
      <span className="bc-row__km">
        {finished ? (
          <Flag aria-hidden="true" className="bc-row__glyph" />
        ) : stale ? (
          <Clock aria-hidden="true" className="bc-row__glyph" />
        ) : null}
        {formatKm(km)}
        <span className="bc-row__unit"> km</span>
        {finished && <span className="sr-only">, finished</span>}
        {stale && !finished && <span className="sr-only">{age ? `, last seen ${age}` : ', not seen lately'}</span>}
      </span>{' '}
      <span className="bc-row__gap">
        {gap}
        {gap && <span className="sr-only"> km behind</span>}
      </span>
    </li>
  )
}
